import { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { Retrospective, User, Project, Sprint, Task } from '../types';
import { toast } from 'sonner';
import { generateId } from '../utils/idGenerator';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import {
  RETRO_INFO_ICON_CLASS,
  RETRO_INFO_SECTION_CLASS,
  RETRO_RISK_ICON_CLASS,
  RETRO_RISK_SECTION_CLASS,
  RETRO_SUCCESS_ICON_CLASS,
  RETRO_SUCCESS_SECTION_CLASS,
  RETRO_WARNING_ICON_CLASS,
  RETRO_WARNING_SECTION_CLASS,
} from '../constants/widgetTheme';

interface CreateRetrospectiveModalProps {
  onClose: () => void;
  onCreate: (retrospective: Retrospective) => Promise<void> | void;
  organizationId: string;
  currentUser: User;
  projects: Project[];
  sprints: Sprint[];
  tasks: Task[];
}

export default function CreateRetrospectiveModal({
  onClose,
  onCreate,
  organizationId,
  currentUser,
  projects,
  sprints,
  tasks,
}: CreateRetrospectiveModalProps) {
  const [type, setType] = useState<'sprint' | 'project' | 'task'>('sprint');
  const [selectedId, setSelectedId] = useState('');
  const [whatWentWell, setWhatWentWell] = useState<string[]>(['']);
  const [whatDidntGoWell, setWhatDidntGoWell] = useState<string[]>(['']);
  const [needsImprovement, setNeedsImprovement] = useState<string[]>(['']);
  const [actionItems, setActionItems] = useState<string[]>(['']);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organizationId) {
      toast.error('No organization selected. Please refresh and try again.');
      return;
    }

    if (availableItems.length > 0 && !selectedId) {
      toast.error(`Please select a ${type}`);
      return;
    }
    
    const retrospective: Retrospective = {
      id: generateId('retrospective'),
      organizationId,
      type,
      ...(type === 'sprint' && { sprintId: selectedId }),
      ...(type === 'project' && { projectId: selectedId }),
      ...(type === 'task' && { taskId: selectedId }),
      participantIds: [currentUser.id],
      whatWentWell: whatWentWell.filter((item) => item.trim() !== ''),
      whatDidntGoWell: whatDidntGoWell.filter((item) => item.trim() !== ''),
      needsImprovement: needsImprovement.filter((item) => item.trim() !== ''),
      actionItems: actionItems.filter((item) => item.trim() !== ''),
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
    };

    await onCreate(retrospective);
  };

  const addItem = (list: string[], setter: (list: string[]) => void) => {
    setter([...list, '']);
  };

  const removeItem = (list: string[], setter: (list: string[]) => void, index: number) => {
    setter(list.filter((_, i) => i !== index));
  };

  const updateItem = (list: string[], setter: (list: string[]) => void, index: number, value: string) => {
    const newList = [...list];
    newList[index] = value;
    setter(newList);
  };

  const getAvailableItems = () => {
    switch (type) {
      case 'sprint':
        return sprints.map((s) => ({ id: s.id, name: s.name }));
      case 'project':
        return projects.map((p) => ({ id: p.id, name: p.name }));
      case 'task':
        return tasks.map((t) => ({ id: t.id, name: t.title }));
      default:
        return [];
    }
  };

  const availableItems = getAvailableItems();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-[1px]">
      <div className="my-8 w-full max-w-4xl rounded-lg border border-border bg-card text-card-foreground shadow-xl">
        <div className="sticky top-0 flex items-center justify-between rounded-t-lg border-border bg-card px-6 py-4">
          <h2 className="text-2xl">Create Retrospective</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
            title="Close retrospective modal"
            aria-label="Close retrospective modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Type Selection */}
          <div>
            <label className="mb-2 block">Retrospective Type *</label>
            <div className="flex gap-3">
              {[
                { value: 'sprint', label: 'Sprint' },
                { value: 'project', label: 'Project' },
                { value: 'task', label: 'Task' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setType(option.value as any);
                    setSelectedId('');
                  }}
                  className={`flex-1 rounded-lg border-2 px-4 py-3 transition-colors ${
                    type === option.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-foreground hover:border-primary/50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Item Selection */}
          {availableItems.length > 0 && (
            <div>
              <label className="mb-2 block">Select {type.charAt(0).toUpperCase() + type.slice(1)} *</label>
              <select
                required
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                title={`Select ${type}`}
                aria-label={`Select ${type}`}
                className={`h-10 w-full ${APP_NATIVE_FIELD_CLASS}`}
              >
                <option value="">Choose...</option>
                {availableItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* What Went Well */}
          <div className={RETRO_SUCCESS_SECTION_CLASS}>
            <div className="flex items-center justify-between mb-3">
              <label className={`block ${RETRO_SUCCESS_ICON_CLASS}`}>What Went Well</label>
              <button
                type="button"
                onClick={() => addItem(whatWentWell, setWhatWentWell)}
                className={`flex items-center gap-1 text-sm ${RETRO_SUCCESS_ICON_CLASS}`}
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            <div className="space-y-2">
              {whatWentWell.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateItem(whatWentWell, setWhatWentWell, index, e.target.value)}
                    placeholder="Something positive from this period..."
                    className={`h-9 flex-1 text-sm ${APP_NATIVE_FIELD_CLASS}`}
                  />
                  {whatWentWell.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(whatWentWell, setWhatWentWell, index)}
                      title="Remove item"
                      aria-label="Remove item"
                      className="rounded-lg p-2 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* What Didn't Go Well */}
          <div className={RETRO_RISK_SECTION_CLASS}>
            <div className="flex items-center justify-between mb-3">
              <label className={`block ${RETRO_RISK_ICON_CLASS}`}>What Didn't Go Well</label>
              <button
                type="button"
                onClick={() => addItem(whatDidntGoWell, setWhatDidntGoWell)}
                className={`flex items-center gap-1 text-sm ${RETRO_RISK_ICON_CLASS}`}
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            <div className="space-y-2">
              {whatDidntGoWell.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateItem(whatDidntGoWell, setWhatDidntGoWell, index, e.target.value)}
                    placeholder="Challenges or issues faced..."
                    className={`h-9 flex-1 text-sm ${APP_NATIVE_FIELD_CLASS}`}
                  />
                  {whatDidntGoWell.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(whatDidntGoWell, setWhatDidntGoWell, index)}
                      title="Remove item"
                      aria-label="Remove item"
                      className="rounded-lg p-2 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Needs Improvement */}
          <div className={RETRO_WARNING_SECTION_CLASS}>
            <div className="flex items-center justify-between mb-3">
              <label className={`block ${RETRO_WARNING_ICON_CLASS}`}>Needs Improvement</label>
              <button
                type="button"
                onClick={() => addItem(needsImprovement, setNeedsImprovement)}
                className={`flex items-center gap-1 text-sm ${RETRO_WARNING_ICON_CLASS}`}
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            <div className="space-y-2">
              {needsImprovement.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateItem(needsImprovement, setNeedsImprovement, index, e.target.value)}
                    placeholder="Areas that need improvement..."
                    className={`h-9 flex-1 text-sm ${APP_NATIVE_FIELD_CLASS}`}
                  />
                  {needsImprovement.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(needsImprovement, setNeedsImprovement, index)}
                      title="Remove item"
                      aria-label="Remove item"
                      className="rounded-lg p-2 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action Items */}
          <div className={RETRO_INFO_SECTION_CLASS}>
            <div className="flex items-center justify-between mb-3">
              <label className={`block ${RETRO_INFO_ICON_CLASS}`}>Action Items</label>
              <button
                type="button"
                onClick={() => addItem(actionItems, setActionItems)}
                className={`flex items-center gap-1 text-sm ${RETRO_INFO_ICON_CLASS}`}
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            <div className="space-y-2">
              {actionItems.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateItem(actionItems, setActionItems, index, e.target.value)}
                    placeholder="What will we improve next time?"
                    className={`h-9 flex-1 text-sm ${APP_NATIVE_FIELD_CLASS}`}
                  />
                  {actionItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(actionItems, setActionItems, index)}
                      title="Remove item"
                      aria-label="Remove item"
                      className="rounded-lg p-2 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border bg-background px-4 py-2 hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Create Retrospective
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}